/**
 * HeaderSearchButton View Component
 *
 * Displays a search input with popover results for player search.
 * @module components/HeaderSearchButton
 */

import { Search, X } from "lucide-react-native";
import React, { memo } from "react";

import { ScrollView } from "@/components/atoms/ScrollView";
import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import {
  Popover,
  PopoverBackdrop,
  PopoverBody,
  PopoverContent,
} from "@/components/atoms/Popover";
import { Pressable } from "@/components/atoms/Pressable";
import { Spinner } from "@/components/atoms/Spinner";
import { Text } from "@/components/atoms/Text";
import { TextInput } from "@/components/atoms/TextInput";

import { PlayerSearchPlayerFragment } from "@/generated/graphql";

import ManuallyAddPlayerOption from "@/components/molecules/ManuallyAddPlayerOption";

import PlayerResultItem from "./PlayerResultItem";

/**
 * Props for HeaderSearchButtonView component
 */
interface HeaderSearchButtonViewProps {
  /** Whether the search popover is open */
  readonly isOpen: boolean;
  /** Handler to open the popover */
  readonly onOpen: () => void;
  /** Handler to close the popover */
  readonly onClose: () => void;
  /** Current search text */
  readonly searchText: string;
  /** Handler for text input changes */
  readonly onChangeText: (text: string) => void;
  /** Handler to clear text input */
  readonly onClearText: () => void;
  /** Search results to display */
  readonly results: PlayerSearchPlayerFragment[];
  /** Whether search is loading */
  readonly isLoading: boolean;
  /**
   * Transfermarkt ID that resolved to no player (SE-3599); switches the
   * empty state to the ID-specific not-found message
   */
  readonly transfermarktNotFoundId?: number | null;
  /** Handler for selecting a player */
  readonly onSelectPlayer: (playerId: string, playerName?: string) => void;
  /** Handler for manually adding a player (hidden when feature-flagged off) */
  readonly onManualAddPlayer?: () => void;
  /** Reference to the input field */
  readonly inputRef: React.RefObject<React.ComponentRef<
    typeof TextInput
  > | null>;
  /** Whether dark theme is active */
  readonly isDark: boolean;
  /** Memoized render function for popover trigger button */
  readonly renderTrigger: (triggerProps: object) => React.JSX.Element;
}

/**
 * Pure view component for header search button with popover
 * @param props - Component props
 * @param props.isOpen - Whether popover is open
 * @param props.onOpen - Handler to open popover
 * @param props.onClose - Handler to close popover
 * @param props.searchText - Current search text
 * @param props.onChangeText - Handler for text changes
 * @param props.onClearText - Handler to clear text
 * @param props.results - Search results
 * @param props.isLoading - Loading state
 * @param props.transfermarktNotFoundId - Transfermarkt ID with no matching player
 * @param props.onSelectPlayer - Handler for player selection
 * @param props.onManualAddPlayer - Handler for manually adding a player
 * @param props.inputRef - Input field reference
 * @param props.isDark - Dark theme flag
 * @param props.renderTrigger - Memoized render function for trigger button
 */
const HeaderSearchButtonView = ({
  isOpen,
  onOpen,
  onClose,
  searchText,
  onChangeText,
  onClearText,
  results,
  isLoading,
  transfermarktNotFoundId,
  onSelectPlayer,
  onManualAddPlayer,
  inputRef,
  isDark,
  renderTrigger,
}: HeaderSearchButtonViewProps) => (
  <Popover
    isOpen={isOpen}
    onClose={onClose}
    onOpen={onOpen}
    placement="bottom"
    offset={8}
    trigger={renderTrigger}
  >
    <PopoverBackdrop />
    <PopoverContent className="w-96 rounded-md bg-surface-base shadow-hard-2">
      <PopoverBody className="p-0">
        <Stack className="gap-0">
          <Stack
            direction="horizontal"
            className="items-center gap-2 border-b border-surface-muted p-3"
          >
            <Stack
              direction="horizontal"
              className="flex-1 items-center gap-2 rounded-sm border border-surface-strong bg-surface-subtle px-3"
            >
              <Icon as={Search} size="lg" className="text-content-muted" />
              <TextInput
                ref={inputRef}
                value={searchText}
                placeholder="Search players..."
                onChangeText={onChangeText}
                // `min-w-0 web:text-ellipsis` is this field's narrow-width
                // failure mode (SE-7731), not decoration. react-native-web
                // renders TextInput as a bare `<input>`, whose min-content
                // width is a UA intrinsic (~20 characters); a flex child's
                // `min-width` computes to `auto`, so `flex-1` alone leaves the
                // field unable to shrink below that and it paints outside this
                // row's border instead. `min-w-0` removes the floor,
                // `web:text-ellipsis` makes the truncation legible. Measured:
                // in a 105px row the field held 136px, and 53px with the fix.
                // Guarded by e2e/tests/se-7731-textinput-narrow-width.spec.ts.
                className="web:text-ellipsis min-w-0 flex-1 py-3 font-body text-content-primary placeholder:text-content-muted"
                autoCorrect={false}
              />
              {searchText.length > 0 && (
                <Pressable onPress={onClearText} className="pl-1">
                  <Icon as={X} size="sm" className="text-content-muted" />
                </Pressable>
              )}
            </Stack>
          </Stack>

          <Box className="max-h-80">
            {isLoading && (
              <Stack
                direction="horizontal"
                className="items-center justify-center gap-2 p-6"
              >
                <Spinner size="small" />
                <Text variant="unstyled" className="text-content-muted">
                  Searching...
                </Text>
              </Stack>
            )}

            {!isLoading && searchText.length < 2 && (
              <Stack className="items-center justify-center p-6">
                <Text
                  variant="unstyled"
                  className="text-center text-content-muted"
                >
                  Type to search for players
                </Text>
              </Stack>
            )}

            {!isLoading && searchText.length >= 2 && results.length === 0 && (
              <Stack>
                <Stack className="items-center justify-center p-6">
                  <Text
                    variant="unstyled"
                    className="text-center text-content-muted"
                  >
                    {transfermarktNotFoundId != null
                      ? `No player found for Transfermarkt ID ${transfermarktNotFoundId}`
                      : `No players found for "${searchText}"`}
                  </Text>
                </Stack>
                {onManualAddPlayer ? (
                  <ManuallyAddPlayerOption
                    subtitle="Can't find who you're looking for?"
                    onPress={onManualAddPlayer}
                    isDark={isDark}
                    variant="inline"
                  />
                ) : null}
              </Stack>
            )}

            {!isLoading && results.length > 0 && (
              <ScrollView
                className="max-h-80"
                bounces={false}
                keyboardShouldPersistTaps="handled"
              >
                {results.map(player => (
                  <PlayerResultItem
                    key={player.id}
                    player={player}
                    onSelect={onSelectPlayer}
                    isDark={isDark}
                  />
                ))}
                {onManualAddPlayer ? (
                  <ManuallyAddPlayerOption
                    subtitle="Can't find who you're looking for?"
                    onPress={onManualAddPlayer}
                    isDark={isDark}
                    variant="inline"
                  />
                ) : null}
              </ScrollView>
            )}
          </Box>
        </Stack>
      </PopoverBody>
    </PopoverContent>
  </Popover>
);

HeaderSearchButtonView.displayName = "HeaderSearchButtonView";

export default memo(HeaderSearchButtonView);
